// AGC: Plot Timeline types and interfaces

export type TimelineEventType = 'milestone' | 'task' | 'note';
export type MilestoneType = 'pre-sowing' | 'sowing' | 'harvest';
export type NoteType = 'text' | 'audio' | 'photo';

// AGC: Base timeline event interface
export interface PlotTimelineEvent {
  id: string;
  type: TimelineEventType;
  eventDate: Date;
  stageCode: string; // e.g., 'VE', 'V1', 'R1'
  title: string;
  description?: string;
  icon?: string;
  isCompleted: boolean;
  pixelPosition?: number; // Calculated vertical position
  offsetY?: number; // Vertical offset for stacking
  zIndex?: number; // Z-index for layering
}

// AGC: Milestone event (sowing, harvest, etc.)
export interface MilestoneEvent extends PlotTimelineEvent {
  type: 'milestone';
  milestoneType: MilestoneType;
}

// AGC: Task event (from existing tasks)
export interface TaskEvent extends PlotTimelineEvent {
  type: 'task';
  taskId: string;
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  dueDate: Date;
  completionDate?: Date;
}

// AGC: Note event (text/audio/photo)
export interface NoteEvent extends PlotTimelineEvent {
  type: 'note';
  noteId: string;
  noteType: NoteType;
  content?: string; // Text content
  audioUrl?: string; // Audio file URL
  photoUrl?: string; // Photo URL
  createdBy: {
    id: string;
    name: string;
    avatar?: string;
  };
  createdAt: Date;
}

// AGC: Timeline configuration
export interface PlotTimelineConfig {
  plotId: string;
  plotName: string;
  cropName: string;
  plantingDate: Date;
  harvestDate: Date;
  cycleStartDate: Date;
  cycleEndDate: Date;
  totalDays: number;
  currentDay: number;
  progressPercent: number;
  stages: GrowthStage[];
}

// AGC: Growth stage definition
export interface GrowthStage {
  code: string; // e.g., 'VE', 'V1', 'R1'
  name: string;
  startDay: number; // Days since planting
  endDay?: number;
  icon?: string;
  color?: string;
}

// AGC: Timeline section (for stage-based grouping)
export interface TimelineSection {
  id: string;
  stageCode: string;
  title: string;
  startDate: Date;
  endDate?: Date;
  events: PlotTimelineEvent[];
  isCurrentStage: boolean;
  height: number; // Calculated height in pixels
}

// AGC: Complete timeline data
export interface PlotTimelineData {
  config: PlotTimelineConfig;
  events: PlotTimelineEvent[];
  sections: TimelineSection[];
}

// AGC: Helper function - Get milestone icon
export const getMilestoneIcon = (type: MilestoneType): string => {
  switch (type) {
    case 'pre-sowing':
      return '🌱';
    case 'sowing':
      return '🌾';
    case 'harvest':
      return '🚜';
    default:
      return '📍';
  }
};

// AGC: Helper function - Get task status icon
export const getTaskStatusIcon = (status: string): string => {
  switch (status) {
    case 'completed':
      return '✓';
    case 'in_progress':
      return '⏳';
    case 'cancelled':
      return '✗';
    default:
      return '○';
  }
};

// AGC: Helper function - Get note type icon
export const getNoteTypeIcon = (type: NoteType): string => {
  switch (type) {
    case 'text':
      return '📝';
    case 'audio':
      return '🎤';
    case 'photo':
      return '📷';
    default:
      return '📄';
  }
};

// AGC: Helper function - Calculate days since planting
export const getDaysSincePlanting = (date: Date, plantingDate: Date): number => {
  return Math.floor((date.getTime() - plantingDate.getTime()) / (24 * 60 * 60 * 1000));
};

// AGC: Helper function - Get stage for a given date
export const getStageForDate = (
  eventDate: Date,
  plantingDate: Date,
  stages: GrowthStage[]
): string => {
  if (eventDate < plantingDate) return 'pre-sowing';

  const daysSincePlanting = getDaysSincePlanting(eventDate, plantingDate);

  for (let i = stages.length - 1; i >= 0; i--) {
    if (daysSincePlanting >= stages[i].startDay) {
      return stages[i].code;
    }
  }

  return stages[0]?.code || 'VE';
};
